import dayjs from 'dayjs'
import Head from 'next/head'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styles from './EncyclopediaArticle.module.scss'
import EncyclopediaNav from 'features/encyclopedia/components/encyclopedia-nav/EncyclopediaNav'
import { ArticleBody, ArticleFooter, ArticleHeader, ArticleIntro, ArticleValve } from 'components/article'
import ContactCTA from 'components/contact-cta/ContactCTA'
import ArticleSection from 'components/article/article-section/ArticleSection'
import { generateArticleStructuredData } from 'utils/structuredData'
import ContentBlocks from 'components/article/content-blocks/ContentBlocks'
import { EncyclopediaArticleProps } from 'features/encyclopedia/article/types'

const EncyclopediaArticle = ({ article, allArticles }: EncyclopediaArticleProps) => {
  // i18n
  const { t } = useTranslation('encyclopedia')

  // Next router
  const { asPath, locale } = useRouter()

  return (
    <>
      {/* SEO */}
      <Head>
        <script
          type="application/ld+json"
          key="article-jsonld"
          dangerouslySetInnerHTML={generateArticleStructuredData({
            ...article,
            authorName: article.author?.firstName,
            datePublished: article.publishedAt,
            dateUpdated: article.updatedAt,
            thumbnail: article.thumbnail.data.attributes.url,
            type: 'Article'
          })}
        />
        <link rel="canonical" href={`https://www.boltenergie.be/${locale}${asPath}`} />
      </Head>

      {/* BREADCRUMBS + SEARCH */}
      <EncyclopediaNav />

      {/* CONTENT */}
      <section className={classNames('container', styles.article)}>
        {/* HEADER */}
        <ArticleHeader
          author={{
            date: dayjs(article.publishedAt).format('DD/MM/YYYY'),
            image: article.author?.avatarUrl,
            name: article.author?.firstName
          }}
          tag={article.category.title}
          title={article.title}
          tagColor={article.category.color}
        />

        {/* INTRO */}
        <ArticleIntro>{parse(article.description)}</ArticleIntro>

        {/* BODY */}
        <ArticleBody>
          <ContentBlocks contentBlocks={article.contentBlocks ?? []} />
        </ArticleBody>

        {/* VALVE (afsluiter) */}
        <ArticleValve>
          <p>
            {t(
              'articleValve',
              'Bolt helpt jou wegwijs maken in energie. Inzicht in je energie-aansluiting en energie algemeen helpen jou om de juiste keuzes te maken richting een klimaatneutrale toekomst.'
            )}
          </p>
        </ArticleValve>

        {/* FOOTER */}
        <ArticleFooter />
      </section>

      {/* AUTHOR */}
      {article?.author && (
        <ContactCTA
          avatarPublicId={article.author.avatarPublicId}
          className={styles['author-contact']}
          description={(article.author?.personalizedFooter?.description as string) ?? t('articleFooterDescription', 'Lorem ipsum')}
          title={
            article.author?.personalizedFooter?.title ??
            t('articleFooterGreeting', 'Hallo, ik ben {{ name }}', { name: article.author.firstName })
          }
        />
      )}

      {/* MORE ARTICLES */}
      <ArticleSection title={t('readMore', 'Meer lezen?')} articles={allArticles} className={styles['more-articles']} />
    </>
  )
}

export default EncyclopediaArticle
